import React, { FC, useEffect, useRef, useState } from 'react';
import { time } from 'uniqid';
import { map } from 'lodash';

export interface TabItemProps {
  label: string;
  isActive: boolean;
  id?: string;
  isDisabled: boolean;
  onClick: (id: string) => void;
  scrollTo: (offset: number) => void;
}
export interface TabProps {
  items: {
    label: string;
    id?: string;
    isDisabled?: boolean;
  }[];
  activeTab: string;
  onClick?: (id: string) => void;
  id?: string;
  className?: string;
}

export const TabItem: FC<TabItemProps> = ({
  label,
  isActive,
  isDisabled = false,
  id = time('tab-item-'),
  onClick,
  scrollTo
}) => {
  return (
    <li role="none" className="d-flex justify-content-start">
      <button
        role="tab"
        onClick={e => {
          e.preventDefault();
          scrollTo((e.target as HTMLButtonElement).offsetLeft);
          onClick(id);
        }}
        className={`p-0 text-nowrap ${isActive ? 'active' : ''}`}
        data-testid={`tab-ele-${id}`}
        id={id}
        aria-controls={id}
        aria-selected={isActive}
        disabled={isDisabled}>
        {label}
      </button>
    </li>
  );
};

export const Tab: FC<TabProps> = ({ items, activeTab, id = time('tab-'), className, onClick = () => true }) => {
  const [showPaddles, setShowPaddles] = useState({ left: false, right: false });
  const tablistRef = useRef<HTMLUListElement>();
  const tabContainerRef = useRef<HTMLDivElement>();
  const scrollScaling = 100;

  const scroll = scrollOffset => {
    tablistRef.current.scrollLeft += scrollOffset;
  };
  const scrollTo = scrollOffset => {
    tablistRef.current.scrollLeft = scrollOffset;
  };

  const adjustSliderPaddles = () => {
    if (tabContainerRef.current && tablistRef.current) {
      const containerWidth = tabContainerRef.current.clientWidth;
      const sliderScrollWidth = tablistRef.current.scrollWidth;
      const sliderScrollLeft = tablistRef.current.scrollLeft;
      const sliderOverflowWidth = Math.floor(sliderScrollWidth - containerWidth);

      if (containerWidth >= sliderScrollWidth) {
        setShowPaddles({ left: false, right: false });
      } else {
        if (sliderScrollLeft <= 0) {
          setShowPaddles({ left: false, right: true });
        } else if (sliderScrollLeft < sliderOverflowWidth) {
          setShowPaddles({ left: true, right: true });
        } else if (sliderScrollLeft >= sliderOverflowWidth) {
          setShowPaddles({ left: true, right: false });
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', adjustSliderPaddles);
    setTimeout(adjustSliderPaddles, 50); //NOSONAR
    return () => {
      window.removeEventListener('resize', adjustSliderPaddles);
    };
  }, []);

  return (
    <div className={`tab w-100 grey-dark-brd ${className}`} ref={tabContainerRef}>
      {showPaddles.left && (
        <div className="paddle paddle-left" tabIndex={-1} onClick={() => scroll(-scrollScaling)}>
          <span className="paddle-icon paddle-icon-left"></span>
        </div>
      )}
      {showPaddles.right && (
        <div className="paddle paddle-right" tabIndex={-1} onClick={() => scroll(scrollScaling)}>
          <span className="paddle-icon paddle-icon-right"></span>
        </div>
      )}
      <ul
        role="tablist"
        className="d-flex justify-content-start p-0 m-0"
        id={id}
        ref={tablistRef}
        onScroll={adjustSliderPaddles}>
        {map(items, item => (
          <TabItem
            label={item.label}
            id={item.id}
            isDisabled={item.isDisabled}
            isActive={item.label === activeTab}
            onClick={onClick}
            scrollTo={offset => {
              scrollTo(offset - tablistRef.current.offsetLeft);
            }}
            key={time('tab-item-')}
          />
        ))}
      </ul>
    </div>
  );
};

export default Tab;
